import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail } from 'lucide-react';

const YDELSER = [
  { name: 'Tømrer', href: '/ydelser/toemrer' },
  { name: 'Tag', href: '/ydelser/tag' },
  { name: 'Døre & Vinduer', href: '/ydelser/doere-vinduer' },
  { name: 'Tilbygning', href: '/ydelser/tilbygning' },
  { name: 'Gulve', href: '/ydelser/gulve' },
  { name: 'Montage', href: '/ydelser/montage' },
  { name: 'Hovedentrepriser', href: '/ydelser/hovedentrepriser' },
];

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();

  // Luk mobilmenuen ved ruteskift
  useEffect(() => {
    setIsOpen(false);
    setDropdownOpen(false);
  }, [location]);

  const isYdelserActive = location.pathname.startsWith('/ydelser');

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/95 backdrop-blur-md">
      {/* Top info bar */}
      <div className="bg-brand-charcoal text-white py-2 px-4 text-xs sm:text-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1 text-slate-300">
              <span className="h-2 w-2 rounded-full bg-brand-primary-light animate-pulse" />
              Malling, Odder, Skanderborg, Ry & Århus
            </span>
          </div>
          <div className="flex items-center gap-6">
            <a href="tel:51219030" className="flex items-center gap-1.5 hover:text-brand-primary-light transition-colors font-medium">
              <Phone className="h-3.5 w-3.5 text-brand-primary-light" />
              <span className="whitespace-nowrap">51 21 90 30</span>
            </a>
            <a href="mailto:kbsoerensen@outlook.dk" className="hidden sm:flex items-center gap-1.5 hover:text-brand-primary-light transition-colors font-medium">
              <Mail className="h-3.5 w-3.5 text-brand-primary-light" />
              <span className="whitespace-nowrap">kbsoerensen@outlook.dk</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="https://assets.auxodev.dk/assets/10033775-ad6c-49dd-a719-01817fe062a0/logo55.png"
              alt="KBS Tømrer-Montage ApS"
              className="h-12 w-auto object-contain transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Menu (Visible >= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-1">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                  isActive
                    ? 'text-brand-primary bg-brand-primary/5'
                    : 'text-brand-charcoal-light hover:text-brand-primary hover:bg-slate-50'
                }`
              }
            >
              Forside
            </NavLink>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                  isActive
                    ? 'text-brand-primary bg-brand-primary/5'
                    : 'text-brand-charcoal-light hover:text-brand-primary hover:bg-slate-50'
                }`
              }
            >
              Om os
            </NavLink>

            {/* Dropdown for Ydelser */}
            <div
              className="relative"
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <button
                className={`flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded-md transition-colors cursor-pointer ${
                  isYdelserActive
                    ? 'text-brand-primary bg-brand-primary/5'
                    : 'text-brand-charcoal-light hover:text-brand-primary hover:bg-slate-50'
                }`}
              >
                Ydelser
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {dropdownOpen && (
                <div className="absolute left-0 mt-0 w-56 rounded-lg border border-slate-100 bg-white p-2 shadow-xl ring-1 ring-black/5 animate-in fade-in slide-in-from-top-1 duration-150">
                  {YDELSER.map((ydelse) => (
                    <NavLink
                      key={ydelse.href}
                      to={ydelse.href}
                      className={({ isActive }) =>
                        `block px-4 py-2.5 text-sm font-medium rounded-md transition-colors ${
                          isActive
                            ? 'text-brand-primary bg-brand-primary/5 font-semibold'
                            : 'text-brand-charcoal-light hover:text-brand-primary hover:bg-slate-50'
                        }`
                      }
                    >
                      {ydelse.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            <NavLink
              to="/galleri"
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                  isActive
                    ? 'text-brand-primary bg-brand-primary/5'
                    : 'text-brand-charcoal-light hover:text-brand-primary hover:bg-slate-50'
                }`
              }
            >
              Galleri
            </NavLink>

            <NavLink
              to="/anbefalinger"
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                  isActive
                    ? 'text-brand-primary bg-brand-primary/5'
                    : 'text-brand-charcoal-light hover:text-brand-primary hover:bg-slate-50'
                }`
              }
            >
              Anbefalinger
            </NavLink>

            <NavLink
              to="/vi-stoetter"
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                  isActive
                    ? 'text-brand-primary bg-brand-primary/5'
                    : 'text-brand-charcoal-light hover:text-brand-primary hover:bg-slate-50'
                }`
              }
            >
              Vi støtter
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `ml-4 px-5 py-2.5 text-sm font-bold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg shadow-md hover:shadow-lg transition-all cursor-pointer ${
                  isActive ? 'ring-2 ring-brand-primary-light ring-offset-2' : ''
                }`
              }
            >
              Kontakt os
            </NavLink>
          </nav>

          {/* Mobile Menu Button (Visible < 1200px) */}
          <div className="flex min-[1200px]:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center rounded-lg p-2.5 text-brand-charcoal hover:bg-slate-100 transition-colors cursor-pointer"
              aria-expanded="false"
            >
              <span className="sr-only">Åbn hovedmenu</span>
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Panel (Visible < 1200px) */}
      {isOpen && (
        <div className="min-[1200px]:hidden border-t border-slate-100 bg-white px-4 py-4 shadow-inner animate-in slide-in-from-top duration-200 max-h-[calc(100vh-130px)] overflow-y-auto overscroll-contain">
          <nav className="flex flex-col gap-1">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `px-4 py-3 text-base font-semibold rounded-lg transition-colors ${
                  isActive ? 'text-brand-primary bg-brand-primary/5' : 'text-brand-charcoal hover:text-brand-primary hover:bg-slate-50'
                }`
              }
            >
              Forside
            </NavLink>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-4 py-3 text-base font-semibold rounded-lg transition-colors ${
                  isActive ? 'text-brand-primary bg-brand-primary/5' : 'text-brand-charcoal hover:text-brand-primary hover:bg-slate-50'
                }`
              }
            >
              Om os
            </NavLink>

            {/* Mobile Dropdown for Ydelser */}
            <div className="flex flex-col">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex w-full items-center justify-between px-4 py-3 text-base font-semibold rounded-lg transition-colors cursor-pointer ${
                  isYdelserActive ? 'text-brand-primary bg-brand-primary/5' : 'text-brand-charcoal hover:text-brand-primary hover:bg-slate-50'
                }`}
              >
                <span>Ydelser</span>
                <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {dropdownOpen && (
                <div className="pl-6 pr-2 py-1 flex flex-col gap-1 bg-slate-50/50 rounded-lg mt-1">
                  {YDELSER.map((ydelse) => (
                    <NavLink
                      key={ydelse.href}
                      to={ydelse.href}
                      className={({ isActive }) =>
                        `block px-4 py-2.5 text-sm font-medium rounded-md transition-colors ${
                          isActive ? 'text-brand-primary bg-brand-primary/5 font-semibold' : 'text-brand-charcoal-light hover:text-brand-primary hover:bg-slate-50'
                        }`
                      }
                    >
                      {ydelse.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            <NavLink
              to="/galleri"
              className={({ isActive }) =>
                `px-4 py-3 text-base font-semibold rounded-lg transition-colors ${
                  isActive ? 'text-brand-primary bg-brand-primary/5' : 'text-brand-charcoal hover:text-brand-primary hover:bg-slate-50'
                }`
              }
            >
              Galleri
            </NavLink>

            <NavLink
              to="/anbefalinger"
              className={({ isActive }) =>
                `px-4 py-3 text-base font-semibold rounded-lg transition-colors ${
                  isActive ? 'text-brand-primary bg-brand-primary/5' : 'text-brand-charcoal hover:text-brand-primary hover:bg-slate-50'
                }`
              }
            >
              Anbefalinger
            </NavLink>

            <NavLink
              to="/vi-stoetter"
              className={({ isActive }) =>
                `px-4 py-3 text-base font-semibold rounded-lg transition-colors ${
                  isActive ? 'text-brand-primary bg-brand-primary/5' : 'text-brand-charcoal hover:text-brand-primary hover:bg-slate-50'
                }`
              }
            >
              Vi støtter
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `mt-3 mx-4 px-5 py-3 text-center text-base font-bold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg shadow-md transition-all cursor-pointer`
              }
            >
              Kontakt os
            </NavLink>
          </nav>
        </div>
      )}
    </header>
  );
}
